import { useState } from "react";
import type { SxProps, Theme } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import AppIconButton from "../../../components/form/AppIconButton";
import { Flag, LANGUAGE_OPTIONS, THEME_MODE_OPTIONS, changeAppLanguage } from "../../../components/AppPreferences";
import { useThemeMode, writeThemeMode } from "../../../hooks/useThemeMode";
import { isDocsPath, resolveHelpTarget } from "../../../navigation/helpTargets";

type Props = {
  /** Icon colour for the trigger (navbar icons are tuned per light / dark mode). */
  iconSx?: SxProps<Theme>;
};

/**
 * Phone / small-tablet replacement for language, theme, and help.
 * Layout settings sit as a dedicated icon on the far right of the navbar.
 */
export default function CompactActionsMenu({ iconSx }: Props) {
  const { t, i18n } = useTranslation();
  const mode = useThemeMode();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const resolved = i18n.resolvedLanguage ?? i18n.language ?? "tr";
  const close = () => setAnchor(null);
  const showHelp = !isDocsPath(pathname);

  return (
    <>
      <AppIconButton aria-label={t("navbar.more")} onClick={(e) => setAnchor(e.currentTarget)} busyIgnore>
        <Icon sx={iconSx}>more_vert</Icon>
      </AppIconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { minWidth: 240, maxWidth: "calc(100vw - 24px)" } } }}
      >
        <ListSubheader disableSticky sx={{ lineHeight: 2.2 }}>
          {t("preferences.language")}
        </ListSubheader>
        {LANGUAGE_OPTIONS.map((option) => (
          <MenuItem
            key={option.code}
            selected={resolved.startsWith(option.code)}
            onClick={() => {
              changeAppLanguage(i18n, option.code);
              close();
            }}
          >
            <ListItemIcon>
              <Flag option={option} />
            </ListItemIcon>
            <ListItemText>{option.label}</ListItemText>
          </MenuItem>
        ))}
        <ListSubheader disableSticky sx={{ lineHeight: 2.2 }}>
          {t("preferences.theme.label")}
        </ListSubheader>
        {THEME_MODE_OPTIONS.map(({ mode: value, icon }) => (
          <MenuItem
            key={value}
            selected={mode === value}
            onClick={() => {
              writeThemeMode(value);
              close();
            }}
          >
            <ListItemIcon>
              <Icon fontSize="small">{icon}</Icon>
            </ListItemIcon>
            <ListItemText>{t(`preferences.theme.${value}`)}</ListItemText>
          </MenuItem>
        ))}
        {showHelp && (
          <>
            <Divider />
            <MenuItem
              onClick={() => {
                close();
                navigate(resolveHelpTarget(pathname, search));
              }}
            >
              <ListItemIcon>
                <Icon fontSize="small">help_outline</Icon>
              </ListItemIcon>
              <ListItemText>{t("docs.helpForPage")}</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
}
